import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { useTheme } from "@repo/tokens";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Box,
  Checkbox,
  CheckboxGroup,
  HStack,
  Icon,
  Text,
} from "@repo/ui";
import {
  formatDateDisplay,
  calendarTodayInTimeZone,
  parseIsoDate,
  toIsoDateString,
} from "./filterFormat";
import { FilterYearGridDropdown } from "./FilterYearGridDropdown";
import { nextMultiSelectValue } from "./filterMultiSelectLogic";
import {
  emptyDoubtsFilterState,
  type DoubtsFilterState,
  type SubjectFilterItem,
} from "./filterTypes";
import { useDoubtData } from "../../../lib/DoubtContext";
import { spacingRem } from "../../../lib/spacingRem";
import { DoubtsAiService } from "@oneapp/data";
import "react-day-picker/style.css";
import "./filter.css";

function normalizeDoubtsFilterForCompare(
  s: DoubtsFilterState,
): DoubtsFilterState {
  return {
    subjects: [...s.subjects].sort((a, b) => a.subjectId - b.subjectId),
    status: [...s.status].sort(),
    startDate: s.startDate,
    endDate: s.endDate,
  };
}

function doubtsFiltersEqual(
  a: DoubtsFilterState,
  b: DoubtsFilterState,
): boolean {
  const na = normalizeDoubtsFilterForCompare(a);
  const nb = normalizeDoubtsFilterForCompare(b);
  return (
    JSON.stringify(na.subjects) === JSON.stringify(nb.subjects) &&
    JSON.stringify(na.status) === JSON.stringify(nb.status) &&
    na.startDate === nb.startDate &&
    na.endDate === nb.endDate
  );
}

const STATUS_FILTER_OPTIONS: readonly {
  apiValue: string;
  labelKey: string;
}[] = [
  { apiValue: "Dismissed", labelKey: "filter_status_dismissed" },
  { apiValue: "Feedback Pending", labelKey: "filter_status_feedback_pending" },
  { apiValue: "In Progress", labelKey: "filter_status_in_progress" },
  { apiValue: "Solved", labelKey: "filter_status_solved" },
] as const;

const ALL_SUBJECTS_SENTINEL = "__all_subjects__";
const ALL_STATUS_SENTINEL = "__all_status__";

function subjectSummary(
  items: SubjectFilterItem[],
  defaultLabel: string,
): string {
  if (items.length > 1) return `${items[0].name}, +${items.length - 1}`;
  if (items.length === 1) return items[0].name;
  return defaultLabel;
}

function statusSummary(
  items: string[],
  allOptions: readonly { apiValue: string; labelKey: string }[],
  t: (key: string) => string,
  defaultLabel: string,
): string {
  if (items.length === 0) return defaultLabel;
  const first = allOptions.find((o) => o.apiValue === items[0]);
  const label = first ? t(first.labelKey) : items[0];
  if (items.length > 1) return `${label}, +${items.length - 1}`;
  return label;
}

function mergeIsoRange(
  prev: DoubtsFilterState,
  mode: "start" | "end",
  iso: string,
): DoubtsFilterState {
  const start = mode === "start" ? iso : prev.startDate;
  const end = mode === "end" ? iso : prev.endDate;
  if (start && end && end < start) {
    return { ...prev, startDate: end, endDate: start };
  }
  return { ...prev, startDate: start, endDate: end };
}

type RawGradeSubject = {
  subjectId?: unknown;
  gradeId?: unknown;
  subject?: { name?: unknown; id?: unknown } | unknown;
  [key: string]: unknown;
};

export type FilterProps = {
  onClose: () => void;
  initialValue?: DoubtsFilterState;
  onApply?: (state: DoubtsFilterState) => void;
  timeZone?: string | null;
};

const FILTER_PANEL_MOTION_MS = 1000;

const Filter = ({ onClose, initialValue, onApply, timeZone }: FilterProps) => {
  const { t } = useTranslation("doubts");
  const { theme } = useTheme();
  const session = useDoubtData();

  const exitingRef = useRef(false);
  const [isExiting, setIsExiting] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const beginClose = useCallback(() => {
    if (exitingRef.current) return;
    exitingRef.current = true;
    setIsExiting(true);
  }, []);

  useEffect(() => {
    if (!isExiting) return;
    const id = window.setTimeout(() => {
      onCloseRef.current();
    }, FILTER_PANEL_MOTION_MS);
    return () => clearTimeout(id);
  }, [isExiting]);

  const accordionTriggerStyle = useMemo(
    () => ({
      background: theme.colors.primary[95],
      borderRadius: 0,
      paddingBlock: spacingRem(theme.spacing.sm),
      paddingInline: spacingRem(theme.spacing.md),
      width: "100%" as const,
    }),
    [theme],
  );

  const appliedBaseline = useMemo<DoubtsFilterState>(
    () => ({
      ...emptyDoubtsFilterState(),
      ...initialValue,
    }),
    [initialValue],
  );

  const [draft, setDraft] = useState<DoubtsFilterState>(() => ({
    ...emptyDoubtsFilterState(),
    ...initialValue,
  }));

  const [dateTarget, setDateTarget] = useState<"start" | "end" | null>(null);
  const datePickerAnchorRef = useRef<HTMLElement | null>(null);

  const emptyFilter = useMemo(() => emptyDoubtsFilterState(), []);
  const applyDisabled = doubtsFiltersEqual(draft, appliedBaseline);
  const resetDisabled = doubtsFiltersEqual(draft, emptyFilter);

  const today = useMemo(() => calendarTodayInTimeZone(timeZone), [timeZone]);

  const calendarBounds = useMemo(() => {
    const year = today.getFullYear();
    return {
      startMonth: new Date(year - 25, 0),
      endMonth: new Date(year, 11, 31),
    };
  }, [today]);

  const dayPickerComponents = useMemo(
    () => ({ YearsDropdown: FilterYearGridDropdown }),
    [],
  );
  const dayPickerFormatters = useMemo(
    () => ({
      formatWeekdayName: (date: Date) =>
        date.toLocaleDateString("en-US", { weekday: "short" }).charAt(0),
    }),
    [],
  );

  // --- Fetch subjects from API ---
  const {
    data: subjectsRaw,
    isLoading: subjectsLoading,
    isError: subjectsError,
    refetch: refetchSubjects,
  } = DoubtsAiService.useGetDc1MetaSubjectsGradeMapping(
    {
      gradeId: session.gradeId ?? 0,
      tenantId: session.tenantId ?? "0",
    },
    {
      request: {
        clientName: "doubtsAi",
      },
      query: {
        enabled: !!session.gradeId && !!session.tenantId,
      },
    },
  );

  const subjectOptions = useMemo<SubjectFilterItem[]>(() => {
    if (!subjectsRaw) return [];
    const candidates = [
      subjectsRaw,
      (subjectsRaw as Record<string, unknown>)?.data,
    ];
    const list = candidates.find(Array.isArray) as
      | RawGradeSubject[]
      | undefined;
    if (!list) return [];
    return list
      .map((item) => {
        const subjectId = Number(item.subjectId);
        const subj = item.subject as { name?: unknown } | undefined;
        const name =
          subj && typeof subj === "object" && "name" in subj
            ? String(subj.name)
            : "";
        if (!Number.isFinite(subjectId) || subjectId <= 0 || !name) {
          return null;
        }
        return { subjectId, name };
      })
      .filter(Boolean) as SubjectFilterItem[];
  }, [subjectsRaw]);

  // --- Build subject checkbox option list (string IDs for CheckboxGroup) ---
  const subjectCheckboxOptions = useMemo(
    () => [
      ALL_SUBJECTS_SENTINEL,
      ...subjectOptions.map((s) => String(s.subjectId)),
    ],
    [subjectOptions],
  );

  const selectedSubjectIds = useMemo(
    () => draft.subjects.map((s) => String(s.subjectId)),
    [draft.subjects],
  );

  const subjectCheckboxValue = useMemo(() => {
    if (
      subjectOptions.length > 0 &&
      selectedSubjectIds.length === subjectOptions.length
    ) {
      return [ALL_SUBJECTS_SENTINEL, ...selectedSubjectIds];
    }
    return selectedSubjectIds;
  }, [selectedSubjectIds, subjectOptions]);

  const subjectAllIndeterminate =
    selectedSubjectIds.length > 0 &&
    selectedSubjectIds.length < subjectOptions.length;

  const handleSubjectChange = (selected: string[]) => {
    const nextIds = nextMultiSelectValue(
      selected,
      subjectCheckboxOptions,
      subjectCheckboxValue,
    );
    const filtered = nextIds.filter((id) => id !== ALL_SUBJECTS_SENTINEL);
    const mapped = filtered
      .map((idStr) => subjectOptions.find((s) => String(s.subjectId) === idStr))
      .filter(Boolean) as SubjectFilterItem[];
    setDraft((d) => ({ ...d, subjects: mapped }));
  };

  // --- Status checkbox ---
  const statusCheckboxOptions = useMemo(
    () => [
      ALL_STATUS_SENTINEL,
      ...STATUS_FILTER_OPTIONS.map((o) => o.apiValue),
    ],
    [],
  );

  const statusCheckboxValue = useMemo(() => {
    if (
      draft.status.length > 0 &&
      draft.status.length === STATUS_FILTER_OPTIONS.length
    ) {
      return [ALL_STATUS_SENTINEL, ...draft.status];
    }
    return draft.status;
  }, [draft.status]);

  const statusAllIndeterminate =
    draft.status.length > 0 &&
    draft.status.length < STATUS_FILTER_OPTIONS.length;

  const handleStatusChange = (selected: string[]) => {
    const nextVals = nextMultiSelectValue(
      selected,
      statusCheckboxOptions,
      statusCheckboxValue,
    );
    setDraft((d) => ({
      ...d,
      status: nextVals.filter((v) => v !== ALL_STATUS_SENTINEL),
    }));
  };

  // --- Labels ---
  const subjectLabel = subjectSummary(draft.subjects, t("subject"));
  const statusLabel = statusSummary(
    draft.status,
    STATUS_FILTER_OPTIONS,
    t,
    t("filter_status"),
  );
  const startLabel = draft.startDate
    ? formatDateDisplay(draft.startDate)
    : t("start_date");
  const endLabel = draft.endDate
    ? formatDateDisplay(draft.endDate)
    : t("end_date");

  const toggleDateTarget = (target: "start" | "end") => {
    setDateTarget((prev) => (prev === target ? null : target));
  };

  useEffect(() => {
    if (!dateTarget) return;
    const frameId = window.requestAnimationFrame(() => {
      datePickerAnchorRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [dateTarget]);

  const activeIso =
    dateTarget === "start"
      ? draft.startDate
      : dateTarget === "end"
        ? draft.endDate
        : "";
  const activeDateTitle =
    dateTarget === "end" ? t("end_date") : t("start_date");
  const activeDateValue = activeIso
    ? formatDateDisplay(activeIso)
    : t("filter_select_date_placeholder");
  const pickerSelected = parseIsoDate(activeIso);
  const displaySelectedDate = pickerSelected ?? today;

  // --- Subject accordion content ---
  let subjectContent: React.ReactNode;
  if (subjectsLoading) {
    subjectContent = (
      <div
        className="filter-accordion-panel"
        style={{ padding: spacingRem(theme.spacing.sm) }}
      >
        <Text>{t("subjects_loading")}</Text>
      </div>
    );
  } else if (subjectsError) {
    subjectContent = (
      <div
        className="filter-accordion-panel"
        style={{
          padding: spacingRem(theme.spacing.sm),
          display: "flex",
          flexDirection: "column",
          gap: spacingRem(theme.spacing.sm),
        }}
      >
        <Text>{t("subjects_load_error")}</Text>
        <button
          type="button"
          className="apply-btn"
          style={{
            alignSelf: "flex-start",
            paddingBlock: spacingRem(theme.spacing.xs),
            paddingInline: spacingRem(theme.spacing.md),
          }}
          onClick={() => void refetchSubjects()}
        >
          {t("subjects_retry")}
        </button>
      </div>
    );
  } else if (subjectOptions.length === 0) {
    subjectContent = (
      <div
        className="filter-accordion-panel"
        style={{ padding: spacingRem(theme.spacing.sm) }}
      >
        <Text>{t("subjects_load_error")}</Text>
      </div>
    );
  } else {
    subjectContent = (
      <div className="filter-accordion-panel">
        <div className="filter-checkbox-group">
          <CheckboxGroup
            gap="lg"
            direction="column"
            value={subjectCheckboxValue}
            onChange={handleSubjectChange}
          >
            <Checkbox
              key={ALL_SUBJECTS_SENTINEL}
              size="md"
              value={ALL_SUBJECTS_SENTINEL}
              label={t("all_subjects")}
              isIndeterminate={subjectAllIndeterminate}
            />
            {subjectOptions.map((opt) => (
              <Checkbox
                key={opt.subjectId}
                size="md"
                value={String(opt.subjectId)}
                label={opt.name}
              />
            ))}
          </CheckboxGroup>
        </div>
      </div>
    );
  }

  const handleBackdropPointerDown = (
    e: React.PointerEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    beginClose();
  };

  return (
    <Box
      className={
        isExiting
          ? "filter-sheet-root filter-sheet-root--exiting"
          : "filter-sheet-root"
      }
    >
      <button
        type="button"
        className="filter-sheet-backdrop"
        aria-label={t("cancel")}
        onPointerDown={handleBackdropPointerDown}
      />
      <Box
        className={
          isExiting
            ? "filter-container filter-container--exiting"
            : "filter-container"
        }
      >
        <div className="filter-inner">
          <div className="filter-header">
            <p className="filter-text">{t("filter")}</p>
            <button
              type="button"
              className="filter-item--interactive"
              onClick={(e) => {
                e.preventDefault();
                beginClose();
              }}
              aria-label={t("cancel")}
              style={{
                background: "none",
                padding: 0,
                display: "flex",
                width: "auto",
              }}
            >
              <Icon
                name="close"
                width={24}
                height={24}
                color={theme.colors.primary[40]}
              />
            </button>
          </div>

          <div className="filter-body">
            <Accordion type="multiple" className="filter-accordion-root">
              <div className="filter-accordion-card">
                <AccordionItem
                  value="subject"
                  className="filter-accordion-item"
                  style={{ borderBottom: "none" }}
                >
                  <AccordionTrigger
                    className="filter-accordion-trigger"
                    style={accordionTriggerStyle}
                  >
                    <Text className="filter-item-text">{subjectLabel}</Text>
                  </AccordionTrigger>
                  <AccordionContent lazy className="filter-accordion-content">
                    {subjectContent}
                  </AccordionContent>
                </AccordionItem>
              </div>

              <div className="filter-dates-section">
                {dateTarget ? (
                  <div className="filter-date-expanded-card">
                    <button
                      type="button"
                      className="filter-date-expanded-header filter-item--interactive"
                      onClick={() => setDateTarget(null)}
                    >
                      <span className="filter-item-text">
                        {activeDateTitle}
                      </span>
                    </button>
                    <div className="filter-date-expanded-value">
                      {activeDateValue}
                    </div>
                    <Box
                      ref={datePickerAnchorRef}
                      className="doubts-filter-daypicker filter-date-expanded-calendar"
                    >
                      <DayPicker
                        mode="single"
                        selected={displaySelectedDate}
                        defaultMonth={displaySelectedDate}
                        today={today}
                        onSelect={(date: Date | undefined) => {
                          if (!date || !dateTarget) return;
                          const iso = toIsoDateString(date);
                          setDraft((d) => mergeIsoRange(d, dateTarget, iso));
                          setDateTarget(null);
                        }}
                        disabled={{ after: today }}
                        showOutsideDays
                        captionLayout="dropdown-years"
                        navLayout="after"
                        weekStartsOn={0}
                        startMonth={calendarBounds.startMonth}
                        endMonth={calendarBounds.endMonth}
                        components={dayPickerComponents}
                        formatters={dayPickerFormatters}
                        modifiersClassNames={{
                          selected: "doubts-rdp-day-selected",
                          today: "doubts-rdp-day-today",
                        }}
                      />
                    </Box>
                  </div>
                ) : (
                  <HStack gap="m">
                    <button
                      type="button"
                      className="filter-item filter-item--interactive"
                      style={{ flex: 1 }}
                      onClick={() => toggleDateTarget("start")}
                    >
                      <span className="filter-item-text">{startLabel}</span>
                    </button>
                    <button
                      type="button"
                      className="filter-item filter-item--interactive"
                      style={{ flex: 1 }}
                      onClick={() => toggleDateTarget("end")}
                    >
                      <span className="filter-item-text">{endLabel}</span>
                    </button>
                  </HStack>
                )}
              </div>

              <div className="filter-accordion-card">
                <AccordionItem
                  value="status"
                  className="filter-accordion-item"
                  style={{ borderBottom: "none" }}
                >
                  <AccordionTrigger
                    className="filter-accordion-trigger"
                    style={accordionTriggerStyle}
                  >
                    <Text className="filter-item-text">{statusLabel}</Text>
                  </AccordionTrigger>
                  <AccordionContent lazy className="filter-accordion-content">
                    <div className="filter-accordion-panel">
                      <div className="filter-checkbox-group">
                        <CheckboxGroup
                          gap="lg"
                          direction="column"
                          value={statusCheckboxValue}
                          onChange={handleStatusChange}
                        >
                          <Checkbox
                            key={ALL_STATUS_SENTINEL}
                            size="md"
                            value={ALL_STATUS_SENTINEL}
                            label={t("all_status")}
                            isIndeterminate={statusAllIndeterminate}
                          />
                          {STATUS_FILTER_OPTIONS.map((opt) => (
                            <Checkbox
                              key={opt.apiValue}
                              size="md"
                              value={opt.apiValue}
                              label={t(opt.labelKey)}
                            />
                          ))}
                        </CheckboxGroup>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </div>
            </Accordion>
          </div>
        </div>

        <div
          className={
            resetDisabled && applyDisabled
              ? "filter-btn-container filter-btn-container--both-disabled"
              : "filter-btn-container"
          }
        >
          <button
            type="button"
            className="clear-btn"
            disabled={resetDisabled}
            onClick={() => {
              setDraft(emptyDoubtsFilterState());
              setDateTarget(null);
            }}
          >
            {t("reset")}
          </button>
          <button
            type="button"
            className="apply-btn"
            disabled={applyDisabled}
            onClick={() => {
              onApply?.(draft);
              beginClose();
            }}
          >
            {t("apply")}
          </button>
        </div>
      </Box>
    </Box>
  );
};

export default Filter;
